import { Suspense } from "react"
import { useController } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Lens } from "@hookform/lenses"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import type { AddressInput } from "@/modules/care-provider/simple-search/services/__generated__/useCareProviderQuickListQuery.graphql"
import { BetterDocCareProviderSelectionBlockedHint } from "@/modules/patients/detail/better-doc/components/better-doc-care-provider-selection-blocked-hint"
import {
  isBetterDocRequestExpected,
  isCareProviderSelectionBlockedByBetterDoc,
} from "@/modules/patients/detail/better-doc/lib/point-of-care-better-doc-rules"
import { usePointOfCareBetterDocContext } from "@/modules/patients/detail/better-doc/point-of-care-better-doc-context"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { type EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { CareProviderSearchType, TimeToTreatType } from "@/types/__generated__/union-types"

type CareProviderContentProps = {
  pointOfCareFormLens: Lens<PointOfCareFormType>
  hasCareProvider?: boolean
  hasMPNReferral?: boolean
  episodeOfCareProgressNoteData?: EpisodeOfCareProgressNoteData
  usePreset?: boolean
  useSpecialty?: boolean
  specialtyId?: string
  searchType: CareProviderSearchType
  showInsurerNetwork?: boolean
  patientAddress?: AddressInput | null
  timeToTreat?: TimeToTreatType
  allowUnknownCareProvider?: boolean
  setCareProvider?: (careProvider: string | null) => void
}

const CARE_PROVIDER_ID_FIELD = "careProviderId"

const BETTER_DOC_REQUIRED_CARE_PROVIDER_MISSING_ERROR =
  "episodeOfCare.progressNote.pointOfCare.betterDocRequiredCareProviderMissing"

export const CareProviderContent = ({
  pointOfCareFormLens,
  hasCareProvider,
  hasMPNReferral,
  episodeOfCareProgressNoteData,
  specialtyId,
  searchType,
  showInsurerNetwork,
  patientAddress,
  timeToTreat,
  allowUnknownCareProvider,
  setCareProvider,
}: CareProviderContentProps) => {
  const { t } = useTranslation()
  const {
    betterDocUsage,
    missingConsultationDetailsThatCanChangeBetterDocUsage,
    guardCareProviderSearch,
    clearSelectedCareProvider,
  } = usePointOfCareBetterDocContext()

  const { field: careProviderIdField, fieldState: careProviderIdFieldState } = useController(
    pointOfCareFormLens.focus(CARE_PROVIDER_ID_FIELD).interop(),
  )
  const { field: betterDocSuggestionStatusField } = useController(
    pointOfCareFormLens.focus("betterDocSuggestionStatus").interop(),
  )

  const isCareProviderSelectionBlocked = isCareProviderSelectionBlockedByBetterDoc({
    missingConsultationDetailsThatCanChangeBetterDocUsage,
    careProviderId: careProviderIdField.value,
    betterDocSuggestionStatus: betterDocSuggestionStatusField.value,
  })

  const getCareProviderError = (): string | undefined => {
    if (isCareProviderSelectionBlocked || !careProviderIdFieldState.error) return undefined
    return isBetterDocRequestExpected({
      betterDocUsage,
      careProviderId: careProviderIdField.value,
      betterDocSuggestionStatus: betterDocSuggestionStatusField.value,
      betterDocRejectionReason: episodeOfCareProgressNoteData?.betterDoc?.rejectionReason,
    })
      ? BETTER_DOC_REQUIRED_CARE_PROVIDER_MISSING_ERROR
      : careProviderIdFieldState.error.message
  }

  const handleSelect = (value: string) => {
    careProviderIdField.onChange(value)
    setCareProvider?.(value)
  }

  const handleRemove = () => {
    clearSelectedCareProvider()
    setCareProvider?.(null)
  }

  return (
    <Suspense fallback={null}>
      <div className="w-full">
        <CareProviderSearch
          label={`${t("episodeOfCare.progressNote.pointOfCare.careProvider")}${hasCareProvider ? "*" : ""}`}
          name={CARE_PROVIDER_ID_FIELD}
          value={careProviderIdField.value}
          onSelect={handleSelect}
          onRemove={handleRemove}
          disabled={isCareProviderSelectionBlocked}
          timeToTreat={timeToTreat}
          searchType={searchType}
          allowUnknownCareProvider={allowUnknownCareProvider}
          betterDocSuggestionStatus={betterDocSuggestionStatusField.value ?? undefined}
          onFocus={guardCareProviderSearch}
          hasMPNReferral={hasMPNReferral}
          showInsurerNetwork={showInsurerNetwork}
          patientAddress={patientAddress}
          defaultAdvancedSearchFilters={{
            hasMPNReferral,
            specialtyId,
          }}
          error={getCareProviderError()}
          specialtyId={specialtyId}
          analyticsSectionName="point_of_care"
          episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        />
        {isCareProviderSelectionBlocked && (
          <BetterDocCareProviderSelectionBlockedHint
            missingConsultationDetailsThatCanChangeBetterDocUsage={
              missingConsultationDetailsThatCanChangeBetterDocUsage
            }
            progressNoteId={episodeOfCareProgressNoteData?.id}
          />
        )}
      </div>
    </Suspense>
  )
}
